import React, { useCallback, useMemo, useState } from 'react';
import AssetFolderForm from './AssetFolderForm';
import AssetCategoryForm from './AssetCategoryForm';
import assetCategoryTree from './data/assetCategoryTree.graphql';
import assetCategory from './data/assetCategory.graphql';
import assetFolder from './data/assetFolder.graphql';
import createAssetFolder from './data/createAssetFolder.graphql';
import updateAssetFolder from './data/updateAssetFolder.graphql';
import deleteAssetFolder from './data/deleteAssetFolder.graphql';
import createAssetCategory from './data/createAssetCategory.graphql';
import updateAssetCategory from './data/updateAssetCategory.graphql';
import deleteAssetCategory from './data/deleteAssetCategory.graphql';
import GqlFullCrudTree from '../../../../../common/components/dataViews/GqlFullCrudTree';
import { catsearch } from '../../../../../common/textSearch';
import useTranslation from '../../../../../common/components/utils/Translations/useTranslation';
import { isRootNode } from '../../../../../common/components/dataViews/DynamicTree';
import { Active } from '../../../../../model/Statuses';
import { TreeLayoutTypeEnum } from '../../../../../common/components/dataViews/BasicTrees/TreeLayoutConfig';
export const AssetCategoryTreeContext = React.createContext({
    assetGroupId: 0,
});
const AssetCategoryTree = ({ assetGroupId, title, organisationGroupId, }) => {
    const [filter, setFilter] = useState({
        searchQuery: '',
        status: Active.value,
    });
    const { t } = useTranslation();
    const makeNameQueryMatcher = useCallback(() => filter.searchQuery
        ? ({ name }) => catsearch(name, filter.searchQuery)
        : null, [filter.searchQuery]);
    const makeStatusMatcher = useCallback(() => filter.status
        ? ({ status }) => status === undefined || status === filter.status
        : null, [filter.status]);
    const predicatesMeta = useMemo(() => [
        {
            valuePropName: 'searchQuery',
            predicate: makeNameQueryMatcher,
            includeChildren: true,
            collectVisibleIds: !!filter.searchQuery,
        },
        {
            valuePropName: 'status',
            predicate: makeStatusMatcher,
            includeChildren: false,
            collectVisibleIds: !!filter.status && !filter.searchQuery,
        },
    ], [
        filter.searchQuery,
        filter.status,
        makeNameQueryMatcher,
        makeStatusMatcher,
    ]);
    const queryVariables = useMemo(() => ({
        assetGroupId,
        organisationGroupId,
    }), [assetGroupId, organisationGroupId]);
    return (<AssetCategoryTreeContext.Provider value={{
        assetGroupId,
    }}>
      <GqlFullCrudTree nodes={{
        folder: {
            canCreateUnder: node => isRootNode(node) || isFolder(node),
            title: t('Folder'),
            isThisNodeType: isFolder,
            component: AssetFolderForm,
            gql: {
                single: assetFolder,
                getParentGql: () => assetFolder,
                create: createAssetFolder,
                update: updateAssetFolder,
                delete: deleteAssetFolder,
            },
        },
        category: {
            canCreateUnder: node => isRootNode(node) || isFolder(node),
            title: t('Category'),
            isThisNodeType: isCategory,
            component: AssetCategoryForm,
            gql: {
                single: assetCategory,
                getParentGql: () => assetFolder,
                create: createAssetCategory,
                update: updateAssetCategory,
                delete: deleteAssetCategory,
            },
        },
    }} tree={{
        rootName: title || '',
        key: 'assetCategoryTree',
        gql: {
            query: assetCategoryTree,
            variables: queryVariables,
        },
        adapter: {
            resolveNodeParentId,
            resolveNodeId,
            nodeIsLeaf,
        },
        plugins: {
            nodeIsSelectable,
        },
        filter: {
            value: filter,
            predicatesMeta,
            onChange: setFilter,
        },
        treeLayoutType: TreeLayoutTypeEnum.TREE_PAGE,
    }}/>
    </AssetCategoryTreeContext.Provider>);
};
export default AssetCategoryTree;
const nodeIsSelectable = node => !isRootNode(node);
export const isCategory = node => (node === null || node === void 0 ? void 0 : node.__typename) === 'AssetCategory';
export const isFolder = node => node.__typename === 'AssetFolder';
const nodeIsLeaf = isCategory;
export const resolveNodeParentId = node => {
    const { parentId, assetFolderId } = node;
    if (isFolder(node) && parentId) {
        return `AssetFolder${parentId}`;
    }
    if (isCategory(node) && assetFolderId) {
        return `AssetFolder${assetFolderId}`;
    }
    return null;
};
export const resolveNodeId = node => node.__typename + node.id;
